import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  useListEscalationRules,
  useCreateEscalationRule,
  useUpdateEscalationRule,
  useDeleteEscalationRule,
  getListEscalationRulesQueryKey,
} from "@workspace/api-client-react";
import type { EscalationRule } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ShieldAlert } from "lucide-react";

// Phase 41 Plan 10 (Wave 7), Task 2 — TCFG-04 Settings CRUD for
// escalation_rule. Firing is Phase 42 — this screen only edits the DATA
// (trigger -> action -> notify-role), matching the config-only scope of
// this plan (41-CONTEXT.md "NOT in this phase": escalation firing).

const TRIGGER_TYPES = [
  { value: "sla_50", label: "50% of SLA elapsed" },
  { value: "sla_breach", label: "SLA breached" },
  { value: "p1_created", label: "P1 task created" },
  { value: "p1_breach", label: "P1 SLA breached" },
  { value: "reopen_ge_2", label: "Reopened 2+ times" },
] as const;
const ALL_ROLES = ["super_admin", "admin", "centralised_team", "agent", "ops_lead", "front_desk", "housekeeping", "bde"] as const;

const CreateSchema = z.object({
  triggerType: z.string().min(1, "Trigger is required"),
  action: z.string().min(1, "Action is required"),
  notifyRole: z.string().nullable().optional(),
});
const EditSchema = z.object({
  triggerType: z.string().min(1, "Trigger is required"),
  action: z.string().min(1, "Action is required"),
  notifyRole: z.string().nullable().optional(),
  active: z.boolean(),
});
type CreateValues = z.infer<typeof CreateSchema>;
type EditValues = z.infer<typeof EditSchema>;

const EMPTY_CREATE: CreateValues = { triggerType: "sla_50", action: "", notifyRole: null };

function errStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}
function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}
function triggerLabel(v: string): string {
  return TRIGGER_TYPES.find((t) => t.value === v)?.label ?? v;
}

export default function TaskEscalationSettingsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "super_admin" || user?.role === "ops_lead";

  const queryClient = useQueryClient();
  // GET is open to any authenticated role server-side — `canEdit` below
  // gates whether this page renders the CRUD UI, not the fetch itself.
  const { data: rules, isLoading } = useListEscalationRules();

  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<EscalationRule | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<EscalationRule | null>(null);

  const createMutation = useCreateEscalationRule();
  const updateMutation = useUpdateEscalationRule();
  const archiveMutation = useDeleteEscalationRule();

  const createForm = useForm<CreateValues>({ resolver: zodResolver(CreateSchema), defaultValues: EMPTY_CREATE });
  const editForm = useForm<EditValues>({
    resolver: zodResolver(EditSchema),
    defaultValues: { triggerType: "sla_50", action: "", notifyRole: null, active: true },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getListEscalationRulesQueryKey() });
  }
  function openEdit(r: EscalationRule) {
    setEditTarget(r);
    editForm.reset({ triggerType: r.triggerType, action: r.action, notifyRole: r.notifyRole ?? null, active: r.active });
  }
  function closeDialogs() {
    setOpenCreate(false);
    setEditTarget(null);
    createForm.reset(EMPTY_CREATE);
  }

  async function onCreate(values: CreateValues) {
    try {
      await createMutation.mutateAsync({ data: values });
      toast.success("Created escalation rule.");
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to add escalation rules.")
          : "Could not create the escalation rule.",
      );
    }
  }
  async function onEdit(values: EditValues) {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data: values });
      toast.success("Escalation rule saved.");
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to edit escalation rules.")
          : "Could not save the escalation rule.",
      );
    }
  }
  async function onArchive() {
    if (!archiveTarget) return;
    try {
      await archiveMutation.mutateAsync({ id: archiveTarget.id });
      toast.success("Archived escalation rule.");
      invalidate();
      setArchiveTarget(null);
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to archive escalation rules.")
          : "Could not archive the escalation rule.",
      );
    }
  }

  if (!canEdit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Restricted</CardTitle>
          <CardDescription>Escalation rule settings are limited to admin and ops lead roles.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Escalation Rules</h1>
            <p className="text-sm text-muted-foreground">
              What happens when SLA thresholds are crossed. Firing itself lands in Phase 42 — this is config only.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpenCreate(true)}>Add Escalation Rule</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rules {rules ? <span className="text-muted-foreground font-normal text-sm">({rules.length})</span> : null}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Notify Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[160px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>{[...Array(5)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
                ))
              ) : rules?.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="h-24 text-center text-muted-foreground">No escalation rules yet.</TableCell></TableRow>
              ) : (
                rules?.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{triggerLabel(r.triggerType)}</TableCell>
                    <TableCell className="text-sm">{r.action}</TableCell>
                    <TableCell className="capitalize text-sm">{r.notifyRole ? r.notifyRole.replace(/_/g, " ") : "— (assignee)"}</TableCell>
                    <TableCell><Badge variant={r.active ? "default" : "outline"}>{r.active ? "Active" : "Archived"}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap pr-4">
                      <div className="flex justify-end items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                        {r.active && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setArchiveTarget(r)}>
                            Archive
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={openCreate || editTarget !== null} onOpenChange={(o) => !o && closeDialogs()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editTarget ? "Edit Escalation Rule" : "Add Escalation Rule"}</DialogTitle></DialogHeader>
          {editTarget ? (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEdit)} className="space-y-3">
                <FormField name="triggerType" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trigger <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {TRIGGER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="action" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Action <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input placeholder="e.g. notify_role, reassign_up" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="notifyRole" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notify Role</FormLabel>
                    <Select value={field.value ?? "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None — targets the task's assignee</SelectItem>
                        {ALL_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="active" control={editForm.control} render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel>Active</FormLabel>
                      <p className="text-xs text-muted-foreground">Off = archived; excluded from Phase 42's evaluation once it lands.</p>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving…" : "Save Rule"}</Button>
                </DialogFooter>
              </form>
            </Form>
          ) : (
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(onCreate)} className="space-y-3">
                <FormField name="triggerType" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trigger <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {TRIGGER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="action" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Action <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input placeholder="e.g. notify_role, reassign_up" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="notifyRole" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notify Role</FormLabel>
                    <Select value={field.value ?? "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None — targets the task's assignee</SelectItem>
                        {ALL_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating…" : "Save Rule"}</Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this escalation rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets it to inactive — removes it from Phase 42's future evaluation once escalation firing lands,
              without deleting the audit trail. Reversible from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} disabled={archiveMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
